import { useEffect, useRef, useState } from "react";

import { TextField, Autocomplete } from "@mui/material";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { Icon } from "@/components";

import { IFilterWithValues } from "../../List/components/FilterModal";
import {
	ICurrentFilter,
	ICurrentFilterValue,
} from "../../List/pages/ListEditFilter/interfaces/config-interface";
import {
	IFilterType,
	IPersonaCategoryFilter,
	IPersonaCategoryFilterWithValues,
} from "../constants/general-filter";

export const usePersonaFilters = ({ filter }: { filter: IPersonaCategoryFilterWithValues }) => {
	const [filterType, setFilterType] = useState<{ [key: string]: IFilterType }>(
		filter?.[0]?.filterParam
			? {
					[filter?.[0]?.filterParam]: filter?.[0]?.type,
				}
			: {}
	);

	const [searchValue, setSearchValue] = useState<{ [key: string]: string }>();
	const [query, setQuery] = useState<{ [key: string]: string }>({});

	const searchTimer = useRef<NodeJS.Timeout | null>(null);

	const counter = useRef<number>(0);

	useEffect(() => {
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			searchValue &&
				Object.keys(searchValue).forEach((key) => {
					setQuery({ ...query, [key]: searchValue[key] });
				});
		}, 800);
	}, [searchValue]);

	const [filterValue, setFilterValue] = useState<IFilterWithValues>({ ...filter });

	const [getFilterByValue] = useLazyGetFilterByPropertyQuery();

	const [inputValues, setInputValues] = useState<string>();

	const [isLoadingByPropertyName, setIsLoadingByPropertyName] = useState<string | null>(null);

	const [currentFilter, setCurrentFilter] = useState<ICurrentFilter>({});

	useEffect(() => {
		const getFilterValues = async () => {
			if (filter) {
				const currentFilterToProcess = Object.values(filter)[0];

				let currentFilterVaules: ICurrentFilter = { ...currentFilter };

				if (
					(currentFilterToProcess.type === "dropdown" ||
						currentFilterToProcess.type === "dropdownExclude" ||
						currentFilterToProcess.type === "dropdownCustom" ||
						currentFilterToProcess.type === "dropdownCustomExclude") &&
					query[currentFilterToProcess.name]?.length > 1
				) {
					setIsLoadingByPropertyName(currentFilterToProcess.name);
					const filterValues = await getFilterByValue({
						propertyName: currentFilterToProcess.name,
						searchTerm: query?.[currentFilterToProcess.name] || "",
					}).unwrap();

					if (filterValues?.data) {
						currentFilterVaules = {
							...currentFilterVaules,
							[currentFilterToProcess.name]: filterValues.data.map((rec) => {
								return {
									id: rec?.id?.toString() || rec.value,
									title: rec.value,
								} as ICurrentFilterValue;
							}) as ICurrentFilterValue[],
						} as ICurrentFilter;
					}

					setIsLoadingByPropertyName(null);
				}

				setCurrentFilter({
					...currentFilterVaules,
				});
			}
		};

		getFilterValues();
	}, [query]);

	const [show, setShow] = useState<number[]>([]);

	const renderFilters = (
		FilterDataType: IPersonaCategoryFilter[],
		hasCheckbox?: boolean,
		noTranslations?: boolean,
		disabled?: boolean
	) => {
		if (FilterDataType?.length > 0) {
			FilterDataType.map((item, index) => {
				if (!show.includes(index) && Object.keys(filterType).includes(item.name)) {
					setShow([...show, index]);
				}
			});
		}

		const renderDropdown = (filter: IPersonaCategoryFilter) => {
			if (filter.filterByType === "dropdown" || filter.filterByType === "dropdownExclude") {
				return (
					<Autocomplete
						multiple
						defaultValue={[]}
						disabled={disabled}
						getOptionLabel={(option) => option.title}
						inputValue={searchValue?.[filter.name] || ""}
						isOptionEqualToValue={(option, currentValue) => {
							if (currentValue.title === "" && currentValue.id === "") {
								return true;
							}

							return option.id === currentValue.id;
						}}
						loading={isLoadingByPropertyName === filter.name}
						loadingText="Searching..."
						noOptionsText={
							searchValue?.[filter.name] && searchValue?.[filter.name]?.length > 0
								? "Typing..."
								: "Start typing to search"
						}
						options={currentFilter?.[filter.name] || []}
						popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
						renderInput={(params) => (
							<TextField
								sx={{
									width: "100%",
									minHeight: 48,
									marginBottom: 1,
									"& .MuiFilledInput-root": {
										backgroundColor: "white",
										minHeight: 48,
										marginBottom: 0,
										borderRadius: 1.5,
										paddingTop: "10px",
										paddingBottom: "10px !important",
										paddingLeft: 1.5,
										"& .MuiChip-root": {
											backgroundColor: "#EAEEFF",
											color: "#1D3DF2",
										},
									},
								}}
								{...params}
							/>
						)}
						size="small"
						sx={{
							width: "100%",
						}}
						value={
							(filterValue?.[filter.name]?.value as {
								title: string;
								id: string;
							}[]) || []
						}
						onChange={(event, value) => {
							setFilterValue({
								...filterValue,
								[filter.name]: {
									value: value,
									type: filter.filterByType,
									filterParam: filter.filterParam,
								},
							});
							counter.current += 1;

							setSearchValue({});
						}}
						onInputChange={(event, newValue, reason) => {
							if (reason === "input") {
								setSearchValue({ [filter.name]: newValue });
							}
						}}
					/>
				);
			}

			return <></>;
		};

		const renderExcludeDropdown = (filter: IPersonaCategoryFilter) => {
			if (
				filter.filterByType === "dropdownCustom" ||
				filter.filterByType === "dropdownCustomExclude"
			) {
				return (
					<Autocomplete
						multiple
						clearOnBlur={true}
						disabled={disabled}
						getOptionLabel={(option) => option.title}
						inputValue={inputValues || ""}
						isOptionEqualToValue={(option, currentValue) => {
							if (currentValue.title === "" && currentValue.id === "") {
								return true;
							}

							return option.id === currentValue.id;
						}}
						loading={isLoadingByPropertyName === filter.name}
						loadingText="Searching..."
						noOptionsText={
							searchValue?.[filter.name] && searchValue?.[filter.name]?.length > 0
								? "Typing..."
								: "Start typing to search"
						}
						options={
							[
								{ id: inputValues || "", title: inputValues || "" },
								...(currentFilter?.[filter.name] || []),
							]?.filter((r) => r.id !== "") || []
						}
						popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
						renderInput={(params) => (
							<TextField
								sx={{
									width: "100%",
									minHeight: 48,
									marginBottom: 1,
									"& .MuiFilledInput-root": {
										backgroundColor: "white",
										minHeight: 48,
										marginBottom: 0,
										borderRadius: 1.5,
										paddingTop: "10px",
										paddingBottom: "10px !important",
										paddingLeft: 1.5,
										"& .MuiChip-root": {
											backgroundColor: "#EAEEFF",
											color: "#1D3DF2",
										},
									},
								}}
								{...params}
							/>
						)}
						size="small"
						sx={{
							width: "100%",
						}}
						value={
							(filterValue?.[filter.name]?.value as {
								title: string;
								id: string;
							}[]) || []
						}
						onBlur={() => {
							setSearchValue({});
						}}
						onChange={(event, value) => {
							setFilterValue({
								...filterValue,
								[filter.name]: {
									value: value,
									type: filter.filterByType,
									filterParam: filter.filterParam,
								},
							});
							counter.current += 1;
							setInputValues("");
							setSearchValue({});
						}}
						onInputChange={(event, newValue, reason) => {
							if (reason === "input") {
								setInputValues(newValue);
								setSearchValue({ [filter.name]: newValue });
							}
						}}
					/>
				);
			}

			return <></>;
		};

		return (
			<div className="flex flex-col w-full">
				{FilterDataType.map((filter) => {
					return (
						<div key={filter.name} className="flex flex-row items-start w-full">
							<>
								{(filter.filterByType === "dropdown" ||
									filter.filterByType === "dropdownExclude") && (
									<div className="flex flex-col w-full ">{renderDropdown(filter)}</div>
								)}
								{(filter.filterByType === "dropdownCustom" ||
									filter.filterByType === "dropdownCustomExclude") && (
									<div className="flex flex-col w-full ">{renderExcludeDropdown(filter)}</div>
								)}
							</>
						</div>
					);
				})}
			</div>
		);
	};

	return {
		renderFilters,
		filterType,
		filterValue,
		setFilterType,
		setFilterValue,
		isDirty: counter.current > 0,
	};
};
